/**
 * Home — origin/destination/date pickers + "Search rides" MainButton.
 */
import { useState } from 'react';
import type { CityDto } from '@waylo/shared';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { localeCountry, useT } from '../lib/i18n';
import { MainButton } from '../lib/MainButton';
import { CityPicker } from '../components/CityPicker';
import { humanDate } from '../lib/datetime';
import { TextInput } from '../components/TextInput';
import { BrandMark } from '../components/Brand';
import { Card } from '../components/Card';
import { HeaderActions } from '../components/HeaderActions';

function todayLocal(): string {
  const d = new Date();
  return formatYmd(d);
}

function tomorrowLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatYmd(d);
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function HomePage(): JSX.Element {
  const { me } = useSession();
  const { navigate } = useRouter();
  const { t, locale } = useT();
  const country = localeCountry(locale);

  const [origin, setOrigin] = useState<CityDto | null>(null);
  const [destination, setDestination] = useState<CityDto | null>(null);
  // Default to tomorrow — most people search the day before they want to travel.
  const [date, setDate] = useState<string>(tomorrowLocal());

  const nameKey: 'nameSr' | 'nameEn' = locale === 'en' ? 'nameEn' : 'nameSr';

  const canSearch = origin && destination && origin.id !== destination.id && date;

  const handleSearch = (): void => {
    if (!origin || !destination) return;
    navigate(`/search/results?o=${origin.id}&d=${destination.id}&date=${date}`);
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <header className="mb-6 flex items-center gap-3">
        <BrandMark size={40} />
        <div className="flex-1 truncate">
          <p className="text-xs text-tg-hint">{t('home.welcomeBack')}</p>
          <p className="truncate font-semibold text-tg-text">
            {me?.displayName ?? t('home.welcomeBack')}
          </p>
        </div>
        <HeaderActions />
      </header>

      <h1 className="text-2xl font-bold text-tg-text">{t('home.title')}</h1>
      <p className="mb-4 text-sm text-tg-hint">{t('home.subtitle')}</p>

      <div className="space-y-3">
        <CityPicker
          value={origin?.id ?? ''}
          selectedCity={origin}
          onChange={(_, c) => setOrigin(c)}
          country={country}
          nameKey={nameKey}
          label={t('home.from')}
          placeholder={t('publish.step.route.via.placeholder')}
          emptyText={t('publish.step.route.via.empty')}
          excludeId={destination?.id}
        />
        <CityPicker
          value={destination?.id ?? ''}
          selectedCity={destination}
          onChange={(_, c) => setDestination(c)}
          country={country}
          nameKey={nameKey}
          label={t('home.to')}
          placeholder={t('publish.step.route.via.placeholder')}
          emptyText={t('publish.step.route.via.empty')}
          excludeId={origin?.id}
        />
        <TextInput
          id="date"
          label={`${t('home.date')} · ${humanDate(date, locale, t)}`}
          type="date"
          min={todayLocal()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* Shortcut grid — role-aware: drivers get Publish + My rides; everyone
          gets Bookings, Leaderboard, Premium. Passengers see a "become a driver"
          tile instead of Publish. */}
      <section className="mt-6 space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-tg-hint">
          {t('home.shortcuts')}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {me?.isDriver ? (
            <Card onClick={() => navigate('/publish')} className="!p-3">
              <p className="font-semibold text-tg-text">{t('home.s.publish.t')}</p>
              <p className="text-xs text-tg-hint">{t('home.s.publish.b')}</p>
            </Card>
          ) : (
            <Card onClick={() => navigate('/profile')} className="!p-3">
              <p className="font-semibold text-tg-text">{t('home.cta.becomeDriver.t')}</p>
              <p className="text-xs text-tg-hint">{t('home.cta.becomeDriver.b')}</p>
            </Card>
          )}
          <Card onClick={() => navigate('/my/bookings')} className="!p-3">
            <p className="font-semibold text-tg-text">{t('home.s.bookings.t')}</p>
            <p className="text-xs text-tg-hint">{t('home.s.bookings.b')}</p>
          </Card>
          {me?.isDriver && (
            <Card onClick={() => navigate('/my/rides')} className="!p-3">
              <p className="font-semibold text-tg-text">{t('home.s.rides.t')}</p>
              <p className="text-xs text-tg-hint">{t('home.s.rides.b')}</p>
            </Card>
          )}
          <Card onClick={() => navigate('/leaderboard')} className="!p-3">
            <p className="font-semibold text-tg-text">{t('home.s.leaderboard.t')}</p>
            <p className="text-xs text-tg-hint">{t('home.s.leaderboard.b')}</p>
          </Card>
          <Card onClick={() => navigate('/premium')} className="!p-3">
            <p className="font-semibold text-tg-text">{t('home.s.premium.t')}</p>
            <p className="text-xs text-tg-hint">{t('home.s.premium.b')}</p>
          </Card>
        </div>
      </section>

      <MainButton text={t('home.search')} onClick={handleSearch} disabled={!canSearch} />
    </div>
  );
}
